import React, { useState, useEffect } from "react";
import { UserCheck, Clock, X, CheckCircle } from "lucide-react";
import Modal from "../../../components/ui/Modal";
import AlertModal from "../../../components/modals/AlertModal";
import UserSelector from "../../../components/selectors/UserSelector";
import { ApiService } from "../../../services/api";
import { useAuth } from "../../../contexts/AuthContext";
import { useModal } from "../../../hooks/useModal";

interface PrivateAppointmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  day: string;
  date: string;
  timeSlot: {
    period: string;
    startTime: string;
    endTime: string;
  };
  slotDuration: number;
  onSuccess?: () => void;
  allowedSubTimes?: string[];
  providerId?: string;
}

const PrivateAppointmentModal: React.FC<PrivateAppointmentModalProps> = ({
  isOpen,
  onClose,
  day,
  date,
  timeSlot,
  slotDuration,
  onSuccess,
  allowedSubTimes,
  providerId,
}) => {
  const { user } = useAuth();
  const { showAlert } = useModal();
  const [selectedUserId, setSelectedUserId] = useState<string>("");
  const [selectedTimeSlots, setSelectedTimeSlots] = useState<string[]>([]);
  const [availableSlots, setAvailableSlots] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showNoSlotsAlert, setShowNoSlotsAlert] = useState(false);

  const formatDateToTurkish = (dateStr: string): string => {
    const [year, month, dayNum] = dateStr.split("-");
    return `${dayNum}.${month}.${year}`;
  };

  const generateTimeSlots = (startTime: string, duration: number): string[] => {
    const [hour, minute] = startTime.split(":").map(Number);
    const slots: string[] = [];
    for (let i = 0; i < 60; i += duration) {
      const mins = (minute + i) % 60;
      const hrs = hour + Math.floor((minute + i) / 60);
      slots.push(`${hrs.toString().padStart(2, "0")}:${mins.toString().padStart(2, "0")}`);
    }
    return slots;
  };

  const addMinutes = (hhmm: string, minsToAdd: number): string => {
    const [h, m] = hhmm.split(":").map(Number);
    const d = new Date(2000, 0, 1, h, m, 0, 0);
    d.setMinutes(d.getMinutes() + minsToAdd);
    const hh = d.getHours().toString().padStart(2, "0");
    const mm = d.getMinutes().toString().padStart(2, "0");
    return `${hh}:${mm}`;
  };

  useEffect(() => {
    if (isOpen) {
      setSelectedUserId("");
      setSelectedTimeSlots([]);
      loadAvailableSlots();
    }
  }, [isOpen, date, timeSlot, allowedSubTimes]);

  const loadAvailableSlots = async () => {
    setLoading(true);
    try {
      const baseSlots =
        allowedSubTimes && allowedSubTimes.length > 0 ? allowedSubTimes : generateTimeSlots(timeSlot.startTime, slotDuration);

      const appointments = providerId
        ? await ApiService.getAppointments({
          providerId,
          startDate: date,
          endDate: date,
          status: "all",
          limit: 1000,
        })
        : await ApiService.getMyProviderAppointments({
          startDate: date,
          endDate: date,
          status: "all",
          limit: 1000,
        });

      const bookedSlots = new Set<string>();
      (appointments.data || []).forEach((apt: any) => {
        if (apt.status === "APPROVED" || apt.status === "PENDING") {
          const startTime = new Date(apt.startDate).toTimeString().slice(0, 5);
          bookedSlots.add(startTime);
        }
      });

      const available = baseSlots.filter((slot) => !bookedSlots.has(slot));
      setAvailableSlots(available);

      if (available.length === 0) {
        setShowNoSlotsAlert(true);
      }
    } catch (error) {
      console.error("Müsait saatler yüklenemedi:", error);
      const fallback =
        allowedSubTimes && allowedSubTimes.length > 0 ? allowedSubTimes : generateTimeSlots(timeSlot.startTime, slotDuration);
      setAvailableSlots(fallback);
    } finally {
      setLoading(false);
    }
  };

  const handleTimeSlotToggle = (time: string) => {
    setSelectedTimeSlots((prev) => (prev.includes(time) ? prev.filter((t) => t !== time) : [...prev, time]));
  };

  const handleSubmit = async () => {
    if (!selectedUserId) {
      await showAlert({
        title: "Uyarı",
        message: "Lütfen bir kullanıcı seçin.",
        type: "warning",
      });
      return;
    }
    if (selectedTimeSlots.length === 0) {
      await showAlert({
        title: "Uyarı",
        message: "Lütfen en az bir saat dilimi seçin.",
        type: "warning",
      });
      return;
    }

    setSubmitting(true);
    try {
      const times = selectedTimeSlots.map((started) => ({
        started,
        ended: addMinutes(started, slotDuration),
      }));

      if (providerId) {
        await ApiService.createAppointmentForUserByAdmin(providerId, {
          userId: selectedUserId,
          date,
          times,
        });
      } else {
        await ApiService.createAppointmentForUser({
          userId: selectedUserId,
          date,
          times,
        });
      }

      await showAlert({
        title: "Başarılı",
        message: "Özel randevu başarıyla oluşturuldu!",
        type: "success",
      });
      onSuccess?.();
      onClose();
    } catch (error: any) {
      await showAlert({
        title: "Hata",
        message: error.message || "Randevu oluşturulurken bir hata oluştu.",
        type: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <AlertModal
        isOpen={showNoSlotsAlert}
        onClose={() => setShowNoSlotsAlert(false)}
        title="Müsait Takvim Bulunamadı"
        message={`${day} günü için ${timeSlot.period} saatinde müsait takvim bulunamadı. Lütfen başka bir gün veya saat dilimi seçiniz.`}
        type="warning"
        buttonText="Tamam"
      />

      <Modal isOpen={isOpen} onClose={onClose} size="xl" title={`Özel Randevu Oluştur - ${day} (${formatDateToTurkish(date)})`}>
        <div className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center space-x-2">
              <UserCheck className="w-5 h-5 text-blue-600" />
              <h3 className="font-semibold text-blue-900">Kullanıcı Seçimi</h3>
            </div>
            <p className="text-sm text-blue-700 mt-1">Randevu oluşturulacak kullanıcıyı seçin</p>
          </div>

          <div className="border border-gray-200 rounded-lg p-4">
            <UserSelector
              value={selectedUserId}
              onChange={(userId) => {
                setSelectedUserId(userId);
              }}
              placeholder="Kullanıcı ara..."
            />
          </div>

          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-4">
              <Clock className="w-5 h-5 text-gray-700" />
              <h3 className="font-semibold text-gray-900">Müsait Saat Dilimleri - {timeSlot.period}</h3>
            </div>

            {loading ? (
              <div className="text-center py-8">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <p className="text-gray-600 mt-2">Yükleniyor...</p>
              </div>
            ) : availableSlots.length === 0 ? (
              <div className="text-center py-8">
                <X className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">Bu saat aralığında müsait zaman dilimi bulunmuyor</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {availableSlots.map((slot) => (
                  <button
                    key={slot}
                    onClick={() => handleTimeSlotToggle(slot)}
                    className={`p-3 rounded-lg border-2 transition-all duration-200 flex items-center justify-between hover:scale-105 active:scale-95 cursor-pointer hover:shadow-md ${selectedTimeSlots.includes(slot)
                        ? "bg-green-50 border-green-200 text-green-700 shadow-sm"
                        : "bg-white border-gray-200 text-gray-700 hover:bg-gray-50"
                      }`}>
                    <span className="font-medium">{slot}</span>
                    {selectedTimeSlots.includes(slot) && <CheckCircle size={18} className="text-green-600" />}
                  </button>
                ))}
              </div>
            )}

            {availableSlots.length > 0 && (
              <div className="mt-4 text-sm text-gray-600">
                <p>
                  Seçili: {selectedTimeSlots.length} saat dilimi
                  {selectedTimeSlots.length > 0 && ` (${selectedTimeSlots.sort().join(", ")})`}
                </p>
              </div>
            )}
          </div>

          <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              onClick={onClose}
              disabled={submitting}
              className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
              İptal
            </button>
            <button
              onClick={handleSubmit}
              disabled={submitting || !selectedUserId || selectedTimeSlots.length === 0}
              className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2">
              {submitting ? (
                <>
                  <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Oluşturuluyor...</span>
                </>
              ) : (
                <>
                  <UserCheck size={18} />
                  <span>Randevu Oluştur</span>
                </>
              )}
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default PrivateAppointmentModal;
